'use client';

import { useAuth } from '@clerk/nextjs';
import { ApiError } from '@lib/api/api-response';
import type { PhoneInUseReason } from '@lib/auth/types/phone-in-use-reason';
import { useMutation, useQueryClient } from '@lib/query/client';
import { toast } from 'sonner';

import { authQueryKeys } from '../auth-query-keys';
import type { UpdateUser } from '../types/update-user';
import type { AuthMe } from './use-auth-me';

function phoneInUseReasonFromApiError(err: ApiError): PhoneInUseReason | null {
  const body = err.response;
  if (!body || typeof body !== 'object') {
    return null;
  }
  const reason = (body as { phoneInUseReason?: unknown }).phoneInUseReason;
  return typeof reason === 'string' ? (reason as PhoneInUseReason) : null;
}

/**
 * Update the current authenticated user (Supabase + Clerk sync) with
 * optimistic cache rollback on error.
 *
 * Owns its own error feedback: callers can use `mutate()` (fire-and-forget)
 * and still get a destructive toast on failure. Callers that need to await
 * the result can use `mutateAsync()`.
 */
export function useUpdateCurrentUser() {
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const queryKey = authQueryKeys.me(userId);

  return useMutation({
    mutationFn: async (data: UpdateUser) => {
      const response = await fetch('/api/protected/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const payload =
          body && typeof body === 'object'
            ? (body as { error?: string; code?: string })
            : {};
        throw new ApiError(
          payload.error || `Failed to update user: ${response.status}`,
          response.status,
          body,
          payload.code
        );
      }
    },
    onMutate: async updates => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<AuthMe>(queryKey);
      // Skip optimistic write when there is no baseline to overlay onto —
      // a partial update merged into nothing would render as a thin user
      // record with most fields missing
      if (prev) {
        queryClient.setQueryData<AuthMe>(queryKey, {
          ...prev,
          user: {
            ...prev.user,
            ...updates,
            // `configurations` is merge-patched server-side;
            // mirror that so a one-key update doesn't drop the
            // other keys from the cached bag.
            ...(updates.configurations
              ? {
                  configurations: {
                    ...prev.user.configurations,
                    ...updates.configurations,
                  },
                }
              : {}),
          },
        });
      }
      return { prev };
    },
    onError: (err, _updates, context) => {
      queryClient.setQueryData(queryKey, context?.prev);
      if (
        err instanceof ApiError &&
        err.code === 'phone_in_use' &&
        phoneInUseReasonFromApiError(err) === 'verification_required'
      ) {
        return;
      }
      toast.error(err.message || 'Failed to update user');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
